import { Suspense } from 'react';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  redirect,
} from 'react-router';

import { LayoutDefault } from 'app/layouts/LayoutDefault/LayoutDefault';
import { MEDICAL_DEVICES_FEATURE } from 'app/providers/featureFlags';

import {
  MedDeviceManufacturerDetailsPage,
  MedDeviceManufacturersPage,
} from 'pages/MedDeviceManufacturers';
import { MedDevicesPage } from 'pages/MedDevices';
import { MedOrgDetailsPage, MedOrgsPage } from 'pages/MedOrg';
import {
  MedOrgInfoTab,
  MedicalDeviceTypeDetailsPage,
  MedicalDevicesTypesPage,
} from 'pages/MedicalDevicesTypes';
import { StatsPage } from 'pages/Stats';
import { UsersPage } from 'pages/User';
import { ErrorPage, InternalServerErrorAsync } from 'pages/error';
import { LoginPage } from 'pages/login/LoginPage';
import { ResetPasswordPage } from 'pages/login/ResetPasswordPage';
import { SetPasswordPage } from 'pages/login/SetPasswordPage';
import { MainPage } from 'pages/main';
import { NotFound } from 'pages/notFound';
import { NotImplemented } from 'pages/notImplemented';

import { PageLoader } from 'widgets/PageLoader';

import { LoginResponse } from 'shared/api/__generated__';
import { NoAccessCoverAsync } from 'shared/ui/NoAccessCover/NoAccessCover.async';
import { PageTitle } from 'shared/ui/PageTitle';

import { hideIfAdminMO } from '../utils';
import { getRedirectByUserRole } from '../utils/common';

function makeRouter() {
  return createBrowserRouter([
    {
      path: '/reset-password',
      loader: () => {
        localStorage.clear();
        return null;
      },
      element: <ResetPasswordPage />,
    },
    {
      path: '/set-password/:resetPasswordId',
      loader: () => {
        localStorage.clear();
        return null;
      },
      errorElement: <ErrorPage />,
      element: <SetPasswordPage />,
    },
    {
      path: '/login',
      loader: () => {
        const isAuthorized = !!localStorage.getItem('USER_DATA');
        if (isAuthorized) return redirect('/');
        return null;
      },
      element: <LoginPage />,
    },

    {
      path: '/login-success',
      loader: () => {
        const userData = localStorage.getItem('USER_DATA');

        // Чистим сохранненые значения фильтров страниц
        sessionStorage.clear();

        if (!userData) return redirect('/login');

        const user: LoginResponse = JSON.parse(userData);

        return redirect(getRedirectByUserRole(user));
      },
    },
    // Корневые и системные страницы должны быть ниже всех в иерархии
    {
      path: '/',
      loader: () => {
        const isAuthorized = !!localStorage.getItem('USER_DATA');
        if (!isAuthorized) return redirect('/login');
        return null;
      },
      errorElement: <ErrorPage />,
      element: <LayoutDefault />,
      children: [
        {
          path: '',
          element: (
            <>
              <PageTitle title='ЕРП - Главная' />
              <MainPage />
            </>
          ),
        },

        {
          path: 'manage',
          children: [
            {
              path: 'server-error',
              element: <InternalServerErrorAsync />,
            },
            {
              path: 'no-access',
              element: <NoAccessCoverAsync />,
            },
            {
              path: 'medical_orgs',
              element: <MedOrgsPage title='Медорганизации' />,
              loader: hideIfAdminMO,
            },
            {
              path: 'medical_org/:id',
              element: <NotImplemented title='Медорганизация' />,
            },
            {
              path: 'users',
              element: <UsersPage title='Пользователи' />,
            },
            {
              path: 'medical_devices_types',
              element: (
                <MedicalDevicesTypesPage title='Типы медицинских изделий' />
              ),
            },
            {
              path: 'medical_devices_types/:id',
              element: <MedicalDeviceTypeDetailsPage />,
            },

            {
              path: 'medical_devices',
              element: <MedDevicesPage title='Медицинские изделия' />,
              loader: () => {
                if (!MEDICAL_DEVICES_FEATURE.canUse)
                  return redirect(MEDICAL_DEVICES_FEATURE.redirect);
                return null;
              },
            },
            {
              path: 'medical_orgs/:id',
              element: <MedOrgDetailsPage />,
              children: [
                {
                  path: 'info',
                  element: <MedOrgInfoTab />,
                },
                {
                  index: true,
                  element: <Navigate to='info' replace />,
                },
              ],
            },

            {
              path: 'markings',
              element: <NotImplemented title='Маркировки' />,
            },

            {
              path: 'medical_device_manufacturers',
              element: (
                <MedDeviceManufacturersPage title='Производители медицинских изделий' />
              ),
              loader: hideIfAdminMO,
            },
            {
              path: 'medical_device_manufacturers/:id',
              element: <MedDeviceManufacturerDetailsPage />,
              loader: hideIfAdminMO,
            },
          ],
        },
        {
          path: 'profile',
          element: <NotImplemented title='Профиль' />,
        },
        {
          path: 'settings',
          element: <NotImplemented title='Настройки' />,
        },
        {
          path: 'stats/:id',
          element: <StatsPage />,
        },
        {
          path: 'logout',
          element: <NotImplemented title='Выход' />,
          loader: () => {
            localStorage.clear();
            return redirect('/login');
          },
        },
      ],
    },
    {
      path: '*',
      element: <NotFound />,
    },
  ]);
}

const routes = makeRouter();

export function AppRouter() {
  return (
    <div className='page-wrapper'>
      <Suspense fallback={<PageLoader />}>
        <RouterProvider router={routes} />
      </Suspense>
    </div>
  );
}
